import { CheckCircle, Circle, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import AvatarWithFallback from "./AvatarWithFallback";
import { Card, CardContent, CardHeader, CardTitle } from "~/atoms/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { WorkflowStepStatuses, type WorkflowStep } from "~/api/types";
import { formatDate } from "~/helpers/date";
import { StatusBadge } from "./WorkflowStatusBadge";
import { WorkflowStepBadge } from "./WorkflowStepBadge";
import { useUserContext } from "~/hooks/useUserContext";
import { printFullName } from "~/helpers/user";
import { getWorkflowStepTooltip } from "~/helpers/workflow-instances";

interface WorkflowStepsListProps {
  steps: WorkflowStep[];
}

export function WorkflowListSteps({ steps }: WorkflowStepsListProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  return (
    <div className="relative">
      <div className="flex snap-x gap-3">
        {steps.map((step) => (
          <Card
            key={step.id}
            className={`border-t-4 ${getStepBorderColor(step.status)} w-[280px] shrink-0 snap-start`}
          >
            <CardHeader className="p-3 pb-1">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {getStepStatusIcon(step.status)}
                    <CardTitle className="text-sm font-medium">
                      {step.name}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={step.status} />
                    {(step.status === WorkflowStepStatuses.active ||
                      step.status === WorkflowStepStatuses.upcoming) &&
                      step.dueAt && (
                        <span className="text-muted-foreground text-xs">
                          {t(($) => $.commons.workflow.stepDueAt, {
                            date: formatDate(step.dueAt),
                          })}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {step.users.length > 0 ? (
                <div className="mt-2 space-y-1">
                  <span className="text-xs font-medium">
                    {t(($) => $.commons.workflow.assigned)}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {step.users.map((user) => {
                      const isCurrentUser = user.userId === currentUser.id;
                      const isActive =
                        step.status === WorkflowStepStatuses.active;
                      const response = user.responseStatus;
                      const tooltip = getWorkflowStepTooltip(
                        response,
                        user,
                        isCurrentUser,
                        t,
                      );

                      return (
                        <TooltipProvider key={user.userId}>
                          <Tooltip>
                            <TooltipTrigger className="cursor-help">
                              <WorkflowStepBadge
                                response={response}
                                isActive={isActive}
                                isCurrentUser={isCurrentUser}
                              >
                                <AvatarWithFallback user={user} size="xs" />
                                <span className="font-normal">
                                  {printFullName(
                                    { firstName: user.firstName },
                                    isCurrentUser,
                                  )}
                                </span>
                              </WorkflowStepBadge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground mt-2 text-xs italic">
                  {t(($) => $.commons.workflow.noUsersAssigned)}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function getStepStatusIcon(status: string) {
  switch (status) {
    case WorkflowStepStatuses.completed:
      return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
    case WorkflowStepStatuses.active:
      return <Clock className="h-3.5 w-3.5 text-blue-500" />;
    case WorkflowStepStatuses.upcoming:
      return <Circle className="h-3.5 w-3.5 text-gray-400" />;
    default:
      return null;
  }
}

function getStepBorderColor(status: string): string {
  switch (status) {
    case WorkflowStepStatuses.completed:
      return "border-green-500";
    case WorkflowStepStatuses.active:
      return "border-blue-500";
    case WorkflowStepStatuses.upcoming:
      return "border-gray-300";
    default:
      return "border-gray-300";
  }
}
